import React from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useAuthStore } from '../lib/auth';
import { useAttendance } from '../hooks/attendance/useAttendance';
import CurrentShiftCard from '../components/attendance/CurrentShiftCard';
import TodaySummaryCard from '../components/attendance/TodaySummaryCard';
import RecentShiftsTable from '../components/attendance/RecentShiftsTable';
import { Button } from '../components/ui/button';
import { attendanceService } from '../services/attendance';

export default function ClockInOutPage() {
  const { user } = useAuthStore();
  const {
    assignedVenues,
    selectedVenueId,
    setSelectedVenueId,
    activeShift,
    recentShifts,
    loading,
    actionLoading,
    error,
    refresh,
    activeVenueLocked,
    clockIn,
    startBreak,
    endBreak,
    clockOut,
  } = useAttendance(user?.id);

  const withActionToast = async (action, successMessage) => {
    try {
      const result = await action();
      toast.success(successMessage);
      return result;
    } catch (err) {
      toast.error(attendanceService.mapErrorMessage(err, 'Action failed'));
      return null;
    }
  };

  const handleClockOut = async () => {
    const result = await withActionToast(clockOut, 'Clocked out successfully');
    const paidHours = Number(result?.paid_hours || result?.total_paid_hours);
    if (result && Number.isFinite(paidHours) && paidHours >= 0) {
      toast.success(`Paid hours: ${paidHours.toFixed(2)}h`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#FF6600] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="clock-in-out-page">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white uppercase tracking-tight">
            Clock In / Clock Out
          </h1>
          <p className="text-[#A1A1AA] mt-1">Track your shift, breaks, and recent hours.</p>
        </div>
        <Button onClick={refresh} variant="secondary" className="self-start" disabled={actionLoading}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-[#FF3B30]/40 bg-[#FF3B30]/10 px-4 py-3 text-[#FFB4AF] text-sm">
          {error}
        </div>
      )}

      {assignedVenues.length === 0 ? (
        <div className="card-rtb border-[#FF3B30]/40">
          <h2 className="font-heading text-2xl uppercase text-white">Venue Assignment Needed</h2>
          <p className="text-[#A1A1AA] mt-2">
            You are not assigned to any venues. Please contact your manager/admin.
          </p>
        </div>
      ) : (
        <>
          <CurrentShiftCard
            assignedVenues={assignedVenues}
            selectedVenueId={selectedVenueId}
            setSelectedVenueId={setSelectedVenueId}
            activeShift={activeShift}
            actionLoading={actionLoading}
            venueLocked={activeVenueLocked}
            onClockIn={() => withActionToast(clockIn, 'Clocked in successfully')}
            onStartBreak={() => withActionToast(startBreak, 'Break started')}
            onEndBreak={() => withActionToast(endBreak, 'Break ended')}
            onClockOut={handleClockOut}
          />

          <TodaySummaryCard activeShift={activeShift} recentShifts={recentShifts} />

          <RecentShiftsTable rows={recentShifts} title="Recent Shifts (Last 14)" />
        </>
      )}
    </div>
  );
}
